import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  useTheme,
  useMediaQuery,
  Button,
  TableCell,
  TableRow,
  Typography,
  Menu,
  MenuItem,
  IconButton,
  Checkbox,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { FIlterIcon } from "../../../assets/Icon/headerCicon";
import { useDispatch } from "react-redux";
import { getBalanceSheet } from "../../../stores/clients";
import { setToast } from "../../../stores/toast/toastslice";

interface CategoryItem {
  total: number;
  count?: number;
  type?: string;
}

interface ApiResponse {
  categories: Record<string, CategoryItem>;
  uncategorized_expenses: {
    total: number;
  };
  uncategorized_income: {
    total: number;
  };
  summary: {
    total_income: number;
    total_expenses: number;
    net_amount: number;
  };
}

const BookkeeperBalanceSheet = ({ val }: { val: (data: any) => void }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const [btnLoading, setBtnLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const [data, setData] = useState<ApiResponse | null>(null);
  const [openFilter, setOpenFilter] = useState(false);
  const [filterValue, setFilterValue] = useState("");
  const [isData, setIsData] = useState(true);

  const dispatch = useDispatch();

  useEffect(() => {
    const getSheet = async () => {
      setLoading(true);
      const res = await dispatch<any>(
        getBalanceSheet({
          year:
            filterValue?.length !== 0
              ? Number(filterValue)
              : new Date()?.getFullYear(),
        }),
      ).unwrap();

      if (res?.code === 200 || res?.code === 404) {
        val(res?.data?.summary);
        setData(res?.data);
        setLoading(false);
        setIsData(true);
      }
      if (res?.data?.code >= 400) {
        setIsData(false);
        dispatch<any>(
          setToast({
            message: res?.data?.message || "Something went wrong",
            type: "error",
            title: "Error",
            onClose: () => {},
          }),
        );
        setLoading(false);
      }
    };
    getSheet();
  }, [filterValue, dispatch]);

  const filterRef = useRef<HTMLDivElement>(null);

  const downloadBalanceSheetCSV = (data: ApiResponse | null) => {
    if (!data) return;
    setBtnLoading(true);

    setTimeout(() => {
      setBtnLoading(false);
    }, 1000);

    // Build a unified object of all exportable categories
    const exportData: Record<string, any> = {};

    // 1️⃣ Add categories dynamically
    if (data.categories && typeof data.categories === "object") {
      Object.entries(data.categories).forEach(([key, value]) => {
        exportData[key] = value;
      });
    }

    // 2️⃣ Add uncategorized entries (if present)
    if (data.uncategorized_expenses) {
      exportData["Uncategorized Expenses"] = data.uncategorized_expenses;
    }
    if (data.uncategorized_income) {
      exportData["Uncategorized Income"] = data.uncategorized_income;
    }

    // CSV rows: header + dynamic categories
    const rows = [
      ["Category", "Amount (USD)"],
      ...Object.entries(exportData).map(([key, value]) => [
        key.replaceAll("_", " "),
        `${
          value?.total < 0
            ? `-$${value?.total?.toString()?.replace("-", "")}`
            : `$${value?.total}` || "0.00"
        }`,
      ]),
      [
        "Net Profit",
        data?.summary?.net_amount < 0
          ? `-$${data.summary.net_amount?.toString()?.replace("-", "")}`
          : `$${(data?.summary?.net_amount || 0).toFixed(2)}`,
        "",
      ],
    ];

    // Generate CSV
    const csvContent = rows.map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "balance_sheet.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    dispatch<any>(
      setToast({
        message: "Balance sheet downloaded!",
        type: "success",
        title: "Success",
        onClose: () => {},
      }),
    );
  };

  if (loading) {
    return (
      <div
        style={{
          margin: "auto",
          display: "flex",
          justifyContent: "center",
          marginTop: 40,
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  const excludedKeys = ["year", "summary"];

  // Recursive function to flatten categories
  // const flattenCategories = (
  //   obj: Record<string, any>,
  //   parentKey: string | null = null
  // ): [string, CategoryData][] => {
  //   let result: [string, CategoryData][] = [];
  //   for (const [key, value] of Object.entries(obj)) {
  //     if (excludedKeys.includes(key)) continue;

  //     if (value && typeof value === "object" && "total" in value) {
  //       // Has a total → it's a category
  //       result.push([
  //         parentKey ? `${parentKey} > ${key}` : key,
  //         value as CategoryData,
  //       ]);
  //     } else if (value && typeof value === "object") {
  //       // Nested object → recurse
  //       result = result.concat(
  //         flattenCategories(value as Record<string, any>, key)
  //       );
  //     }
  //   }
  //   return result;
  // };

  const categoryEntries = Object.entries(data?.categories || {}).filter(
    ([, value]) => value?.total !== undefined,
  );

  return (
    <div
      style={{
        backgroundColor: "#ffffff",
        borderRadius: "12px",
        padding: 20,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <p
          style={{
            fontSize: "20px",
            color: "#01060B",
            fontWeight: 600,
          }}
        >
          Balance Sheet
        </p>
        {/**FILTERS */}
        <div ref={filterRef} style={{ position: "relative" }}>
          {/* Red dot indicator when filter is applied */}
          {false && (
            <span
              style={{
                position: "absolute",
                top: 8,
                left: 8,
                width: 10,
                height: 10,
                background: "#F44336",
                borderRadius: "50%",
                zIndex: 2,
                border: "2px solid #fff",
              }}
            />
          )}
          <Button
            style={{
              border: "1px solid #EAF1FA",
              borderRadius: "8px",
              height: "40px",
              padding: 15,
              color: "#6C6F71",
              fontSize: "12px",
              fontWeight: 600,
              gap: 10,
            }}
            onClick={() => {
              setOpenFilter(!openFilter);
            }}
          >
            {filterValue?.length ? (
              <p
                style={{
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                {filterValue}
              </p>
            ) : (
              <p
                style={{
                  alignItems: "center",
                  display: "flex",
                  gap: 10,
                }}
              >
                <FIlterIcon />
                Filter by Year
              </p>
            )}
          </Button>
          {openFilter && (
            <div
              style={{
                position: "absolute",
                marginTop: 10,
                backgroundColor: "white",
                borderRadius: "8px",
                width: isMobile ? "280px" : "400px",
                right: isMobile ? "" : "0px",
                zIndex: 1000,
                boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
                marginBottom: 20,
              }}
            >
              {openFilter && (
                <Menu
                  open={openFilter}
                  anchorEl={filterRef.current}
                  onClose={() => setOpenFilter(false)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  PaperProps={{
                    style: {
                      borderRadius: "8px",
                      marginTop: 10,
                      width: "200px",
                      boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
                    },
                  }}
                >
                  <p
                    style={{
                      padding: 10,
                      paddingTop: 5,
                      paddingLeft: 16,
                      borderBottom: "1px solid #EDF2F6",
                      color: "#01060B",
                      fontSize: "16px",
                      fontWeight: 500,
                    }}
                  >
                    Amount (USD)
                  </p>
                  {["2025", "2024"].map((year, idx) => (
                    <MenuItem
                      key={year}
                      onClick={() => {
                        console.log("Filter by year:", year);
                        setOpenFilter(false);
                        setFilterValue(year);
                      }}
                      sx={{
                        borderRadius: "6px",
                        "&:hover": {
                          backgroundColor: "#A7C7EA66", // light blue hover
                          color: "#1D538F",
                          borderRadius: 0,
                        },
                        borderBottom:
                          idx <= ["2025", "2024"]?.length - 2
                            ? "1px solid #EDF2F6"
                            : "",
                        color: "#6C6F71",
                        fontWeight: 500,
                        fontSize: "16px",
                      }}
                    >
                      <div
                        style={{
                          width: "100%",
                          paddingTop: 2,
                          paddingBottom: 2,
                        }}
                      >
                        {year}
                      </div>
                    </MenuItem>
                  ))}
                </Menu>
              )}
            </div>
          )}
        </div>
      </div>
      {/**BALANCE SHEET */}
      {isData ? (
        <div
          style={{
            border: "1px solid #EAF1FA",
            borderRadius: "8px",
            marginTop: 10,
            padding: 20,
          }}
        >
          {/* Fixed Header */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <p
              style={{
                fontWeight: 500,
                fontSize: "16px",
              }}
            >
              Category
            </p>
            <p
              style={{
                fontWeight: 500,
                fontSize: "14px",
              }}
            >
              Amount (USD)
            </p>
          </div>

          {/* Dynamic Rows */}
          {categoryEntries.map(([key, value], index) => (
            <div
              key={key}
              style={{
                marginTop: 20,
                fontWeight: 500,
                fontSize: "14px",
                color: "#6C6F71",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderTop: "1px solid #EAF1FACC",
                paddingTop: 14,
                ...(index === categoryEntries.length - 1 && {
                  borderBottom: "1px solid #EAF1FACC",
                  paddingBottom: 14,
                }),
              }}
            >
              <p style={{ textTransform: "capitalize" }}>
                {key.replaceAll("_", " ")}
              </p>
              <p>
                {value?.total < 0
                  ? `-$${value?.total?.toString()?.replace("-", "")}`
                  : `$${value?.total}` || "0.00"}
              </p>
            </div>
          ))}

          <div
            style={{
              marginTop: 20,
              fontWeight: 500,
              fontSize: "14px",
              color: "#6C6F71",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid #EAF1FACC",
              paddingBottom: 14,
            }}
          >
            <p style={{ textTransform: "capitalize" }}>Uncategorised Income</p>
            <p>${data?.uncategorized_income?.total || "0.00"}</p>
          </div>

          <div
            style={{
              marginTop: 20,
              fontWeight: 500,
              fontSize: "14px",
              color: "#6C6F71",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid #EAF1FACC",
              paddingBottom: 14,
            }}
          >
            <p style={{ textTransform: "capitalize" }}>
              Uncategorised Expenses
            </p>
            <p>
              {data?.uncategorized_expenses &&
              data?.uncategorized_expenses?.total < 0
                ? `-$${data?.uncategorized_expenses?.total
                    ?.toString()
                    ?.replace("-", "")}`
                : `$${data?.uncategorized_expenses?.total}` || "0.00"}
            </p>
          </div>

          {/* Net Profit Summary */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 3,
              backgroundColor: "#EAF1FA99",
              borderRadius: "10px",
              padding: 20,
              marginTop: 24,
            }}
          >
            <p
              style={{
                fontWeight: 700,
                fontSize: "16px",
                width: "100px",
                color: "#2977CC",
              }}
            >
              Net Profit
            </p>

            <div
              style={{
                width: "100%",
                height: 1,
                backgroundColor: "#83B0E166",
              }}
            />
            <p
              style={{
                fontWeight: 700,
                fontSize: "16px",
                color: "#2977CC",
                marginLeft: 5,
              }}
            >
              {(data?.summary?.net_amount || 0) < 0
                ? `-$${
                    data?.summary?.net_amount?.toString()?.replace("-", "") || 0
                  }`
                : `$${(data?.summary?.net_amount || 0).toFixed(2)}`}
            </p>
          </div>
        </div>
      ) : (
        <div
          style={{
            border: "1px solid #EAF1FA",
            borderRadius: "8px",
            marginTop: 10,
            padding: 20,
          }}
        >
          <div
            style={{
              margin: "auto",
              display: "flex",
              justifyContent: "center",
            }}
          >
            No data found!
          </div>
        </div>
      )}
      {isData && (
        <Button
          style={{
            background: "linear-gradient(to right, #2977CC, #1D538F)",
            fontWeight: 600,
            fontSize: "16px",
            color: "#ffffff",
            paddingTop: "12px",
            paddingBottom: "12px",
            paddingLeft: "45px",
            paddingRight: "45px",
            marginTop: 30,
            marginBottom: 16,
          }}
          onClick={() => downloadBalanceSheetCSV(data)}
        >
          {btnLoading ? (
            <span
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              <CircularProgress size={22} sx={{ color: "#fff" }} />
            </span>
          ) : (
            "Download"
          )}
        </Button>
      )}
    </div>
  );
};

export default BookkeeperBalanceSheet;
